import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import styles from "./DemoRequestCreate.module.css";
import {
  CustomButton,
  CustomDropdown,
  CustomTextInput,
} from "../../components";
import { AxiosInstance } from "../../services/Api";
import shape1 from "../../assets/images/shape1.png";
import { fetchCountryOptions } from "../../redux/Masterdata";
import PopupModal from "../../components/PopupModal/PopupModal";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const DemoRequestCreate = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth", // Smooth scrolling
    });
  }, []); // Empty dependency array ensures this runs only once when component mounts
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { countryOptions: reduxCountryOptions, countryLoading } = useSelector(
    (state) => state.masterData,
  );

  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    designation: "",
    email: "",
    phone: "",
    institutionName: "",
    country: null,
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success", // "success" | "error"
    message: "",
    redirectPath: "",
  });
  useEffect(() => {
    dispatch(fetchCountryOptions());
  }, [dispatch]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const numericValue = value.replace(/\D/g, "");
    const limitedValue = numericValue.slice(0, 10);
    handleInputChange("phone", limitedValue);
  };

  const handleDropdownChange = (field, selectedValue) => {
    handleInputChange(field, selectedValue);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }
    if (!formData.designation.trim()) {
      newErrors.designation = "Designation is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Mobile number is required";
    } else if (formData.phone.length !== 10) {
      newErrors.phone = "Please enter a valid 10-digit mobile number";
    }
    if (!formData.institutionName.trim()) {
      newErrors.institutionName = "Institution name is required";
    }
    if (!formData.country) {
      newErrors.country = "Please select a location";
    }
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email: formData.email.trim(),
        designation: formData.designation.trim(),
        institution_name: formData.institutionName.trim(),
        country: formData.country?.value || "",
        phone: `+91 ${formData.phone}`,
        message: formData.message.trim(),
      };

      const response = await AxiosInstance.post("roll/demo-request", payload);

      if (response.data?.success) {
        setModalState({
          open: true,
          type: "success",
          message:
            "Demo request submitted successfully! We will contact you soon.",
          redirectPath: "",
        });
        setFormData({
          firstName: "",
          middleName: "",
          lastName: "",
          designation: "",
          email: "",
          phone: "",
          institutionName: "",
          country: null,
          message: "",
        });
        setErrors({});
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response.data?.message || "Failed to submit demo request",
          redirectPath: "",
        });
        throw new Error(
          response.data?.message || "Failed to submit demo request",
        );
      }
    } catch (error) {
      console.error("Error submitting demo request:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to submit demo request. Please try again.";
      setModalState({
        open: true,
        type: "error",
        message: errorMessage,
        redirectPath: "",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };
  return (
    <>
      <Helmet>
        <title>Get a free demo consultation | iFillip - school management system</title>
        <meta
          name="description"
          content="Get iFillip’s free demo consultation and unlock an all-in-one school management system. Simplify administration, enhance communication, and streamline academic workflows smarter"
        />
      </Helmet>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.pageContainer}>
        <div className={styles.contentWrapper}>
          <div className={styles.formSection}>
            <div className={styles.formHeader}>
              <h2 className={styles.formTitle}>Want a demo?</h2>
              <p className={styles.formSubtitle}>
                Please share your details and we'll connect with you
              </p>
            </div>

            <form className={styles.form} onSubmit={handleSubmit}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <CustomTextInput
                    placeholder="First Name"
                    value={formData.firstName}
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                    error={errors.firstName}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <CustomTextInput
                    placeholder="Middle Name"
                    value={formData.middleName}
                    onChange={(e) =>
                      handleInputChange("middleName", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <CustomTextInput
                    placeholder="Last Name"
                    value={formData.lastName}
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                    error={errors.lastName}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <CustomTextInput
                    placeholder="Your Designation"
                    value={formData.designation}
                    onChange={(e) =>
                      handleInputChange("designation", e.target.value)
                    }
                    error={errors.designation}
                    required
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <CustomTextInput
                    type="email"
                    placeholder="Your Email ID"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    error={errors.email}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <CustomTextInput
                    type="text"
                    placeholder="Your Mobile Number"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    error={errors.phone}
                    maxLength={10}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    required
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <CustomTextInput
                    placeholder="Institution Name"
                    value={formData.institutionName}
                    onChange={(e) =>
                      handleInputChange("institutionName", e.target.value)
                    }
                    error={errors.institutionName}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <CustomDropdown
                    options={reduxCountryOptions}
                    value={formData.country}
                    onChange={(value) => handleDropdownChange("country", value)}
                    placeholder="--Select Location--"
                    error={errors.country}
                    isLoading={countryLoading}
                    required
                  />
                </div>
              </div>

              <div className={styles.formRowFull}>
                <div className={styles.formGroupFull}>
                  <CustomTextInput
                    type="textarea"
                    placeholder="Short your personal message"
                    value={formData.message}
                    onChange={(e) =>
                      handleInputChange("message", e.target.value)
                    }
                    error={errors.message}
                    rows={3}
                    required
                  />
                </div>
              </div>

              <div className={styles.formActions}>
                <CustomButton
                  type="submit"
                  variant="outlined"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Request Demo"}
                </CustomButton>
              </div>
            </form>
          </div>

          <div className={styles.infoSection}>
            <div className={styles.infoContent}>
              <h2 className={styles.infoTitle}>Putting Education First</h2>
              <p className={styles.infoDescription}>
                iFillip is a digital meeting place for the stakeholders –
                Students, Parents, Teachers & School management. Easy- to-use
                features enables seamless and active sharing of information &
                notes across the various stakeholders on any device apart
              </p>
              <div className={styles.illustrationWrapper}>
                <img
                  src={shape1}
                  alt="Education Illustration"
                  className={styles.illustration}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DemoRequestCreate;
